import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Paper,
  Typography,
  LinearProgress,
  Chip,
  Stack,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Card,
  CardContent,
  Tooltip,
  IconButton,
  Popover,
  Divider,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  ExpandMore as ExpandMoreIcon,
  Psychology as AIIcon,
  Timer as TimerIcon,
  TextFields as ContentIcon,
  Tag as HashtagIcon,
  Image as VisualIcon,
  TrendingUp as EngagementIcon,
  Lightbulb as TipIcon,
  AutoFixHigh as SuggestionIcon,
  Info as InfoIcon,
  Calculate as CalculateIcon
} from '@mui/icons-material';

// Strict and transparent content scoring engine
class StrictContentScorer {
  constructor() {
    this.weights = {
      length: 0.25,
      readability: 0.20,
      engagement: 0.20,
      structure: 0.15,
      hashtags: 0.10,
      emotion: 0.10
    };
  }

  analyzeContent(content, hasImage = false, hashtags = []) {
    if (!content || content.trim().length === 0) {
      return this.getEmptyScore();
    }

    const analysis = {
      length: this.scoreLengthOptimization(content),
      readability: this.scoreReadability(content),
      engagement: this.scoreEngagementFactors(content),
      structure: this.scoreStructure(content),
      hashtags: this.scoreHashtags(content, hashtags),
      emotion: this.scoreEmotionalAppeal(content),
      visual: hasImage ? 100 : 60 // Stricter penalty for no image
    };

    const overallScore = this.calculateOverallScore(analysis);
    const suggestions = this.generateSuggestions(analysis, content, hasImage);
    const strengths = this.identifyStrengths(analysis);
    const breakdown = this.getScoreBreakdown(analysis);

    return {
      overallScore: Math.round(overallScore),
      categoryScores: analysis,
      suggestions,
      strengths,
      metrics: this.getDetailedMetrics(content, hashtags),
      grade: this.getGrade(overallScore),
      estimatedEngagement: this.estimateEngagement(overallScore, hasImage),
      breakdown
    };
  }

  scoreLengthOptimization(content) {
    const charCount = content.length;
    const wordCount = content.split(/\s+/).filter(w => w.length > 0).length;
    
    // More realistic length requirements for LinkedIn
    if (charCount < 50) return 20; // Very poor for too short
    if (charCount >= 150 && charCount <= 600) return 100; // Optimal range for engagement
    if (charCount >= 50 && charCount < 150) return 70; // Decent but short
    if (charCount >= 600 && charCount <= 1000) return 90; // Good, thought leadership length
    if (charCount >= 1000 && charCount <= 1500) return 85; // Long but acceptable for stories
    if (charCount >= 1500 && charCount <= 2000) return 70; // Getting quite long
    if (charCount >= 2000 && charCount <= 3000) return 50; // Very long, may lose attention
    return 30; // Extremely long
  }

  scoreReadability(content) {
    const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const words = content.split(/\s+/).filter(w => w.length > 0);
    
    if (sentences.length === 0 || words.length === 0) return 40;

    const avgWordsPerSentence = words.length / sentences.length;
    const complexWords = words.filter(word => word.length > 6).length; // Stricter threshold
    const complexityRatio = complexWords / words.length;

    let score = 60; // Lower base score
    
    // Stricter penalties for readability
    if (avgWordsPerSentence >= 8 && avgWordsPerSentence <= 15) score += 25; // Optimal
    if (avgWordsPerSentence >= 15 && avgWordsPerSentence <= 20) score += 10; // Acceptable
    if (avgWordsPerSentence > 20) score -= 20; // Too long
    if (avgWordsPerSentence < 5) score -= 15; // Too choppy
    
    if (complexityRatio < 0.2) score += 15; // Simple language bonus
    if (complexityRatio > 0.3) score -= 15; // Complex language penalty
    if (complexityRatio > 0.5) score -= 25; // Very complex penalty

    return Math.max(10, Math.min(100, score));
  }

  scoreEngagementFactors(content) {
    let score = 40; // Lower base score
    const lowerContent = content.toLowerCase();

    // Check for questions - more selective
    const questionCount = (content.match(/\?/g) || []).length;
    const meaningfulQuestions = content.match(/what|how|why|when|where|which|who.*\?/gi) || [];
    score += Math.min(questionCount * 8, 20);
    score += Math.min(meaningfulQuestions.length * 5, 15);

    // Check for call-to-action words - stricter requirements
    const strongCtaWords = ['comment', 'share', 'thoughts', 'agree', 'disagree', 'experience', 'think', 'opinion'];
    const weakCtaWords = ['like', 'follow'];
    const strongCtaCount = strongCtaWords.filter(word => lowerContent.includes(word)).length;
    const weakCtaCount = weakCtaWords.filter(word => lowerContent.includes(word)).length;
    score += Math.min(strongCtaCount * 10, 25);
    score += Math.min(weakCtaCount * 3, 10);

    // Check for personal stories/experiences - more selective
    const personalWords = ['i', 'my', 'me', 'personally', 'experience', 'learned', 'discovered', 'realized'];
    const personalCount = personalWords.filter(word => lowerContent.includes(word)).length;
    score += Math.min(personalCount * 2, 10);

    // Check for numbers/statistics - require context
    const numberMatches = content.match(/\d+%|\d+\.\d+%|\d+x|\d+ years?|\$\d+|\d+k|\d+m/g) || [];
    score += Math.min(numberMatches.length * 8, 20);

    // Penalty for generic content
    const genericPhrases = ['great post', 'nice share', 'thanks for sharing', 'good point'];
    const genericCount = genericPhrases.filter(phrase => lowerContent.includes(phrase)).length;
    score -= genericCount * 10;

    return Math.max(10, Math.min(100, score));
  }

  scoreStructure(content) {
    let score = 30; // Lower base score

    // Check for bullet points or lists - more selective
    const bulletPoints = content.match(/^[\s]*[•\-\*→]\s/gm) || [];
    const numberedLists = content.match(/^\s*\d+[\.\)]\s/gm) || [];
    if (bulletPoints.length >= 2) score += 20;
    if (numberedLists.length >= 2) score += 20;
    if (bulletPoints.length === 1 || numberedLists.length === 1) score += 10;

    // Check for line breaks - require meaningful breaks
    const paragraphs = content.split('\n\n').filter(p => p.trim().length > 0);
    if (paragraphs.length >= 2) score += 15;
    if (paragraphs.length >= 3) score += 10;

    // Check for emojis - moderate use
    const emojiRegex = /[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]/gu;
    const emojiCount = (content.match(emojiRegex) || []).length;
    if (emojiCount >= 1 && emojiCount <= 5) score += 15;
    if (emojiCount > 5) score -= 10; // Too many emojis

    // Check for clear opening - stricter requirements
    const firstSentence = content.split(/[.!?]/)[0];
    if (firstSentence && firstSentence.length >= 20 && firstSentence.length <= 60) score += 15;

    // Check for strong conclusion
    const lastSentence = content.split(/[.!?]/).filter(s => s.trim().length > 0).pop();
    if (lastSentence && (lastSentence.includes('?') || lastSentence.toLowerCase().includes('thoughts'))) {
      score += 10;
    }

    return Math.max(10, Math.min(100, score));
  }

  scoreHashtags(content, additionalHashtags = []) {
    const contentHashtags = (content.match(/#[\w-]+/g) || []).length;
    const totalHashtags = contentHashtags + additionalHashtags.length;

    // Stricter hashtag requirements
    if (totalHashtags === 0) return 30; // Poor for no hashtags
    if (totalHashtags >= 3 && totalHashtags <= 5) return 100; // Optimal range
    if (totalHashtags >= 1 && totalHashtags <= 2) return 70; // Decent but few
    if (totalHashtags >= 6 && totalHashtags <= 8) return 80; // Good but many
    if (totalHashtags > 8) return 50; // Too many hashtags

    return 60;
  }

  scoreEmotionalAppeal(content) {
    let score = 30; // Lower base score
    const lowerContent = content.toLowerCase();

    // Positive emotional words - more selective
    const strongPositiveWords = ['thrilled', 'incredible', 'amazing', 'breakthrough', 'revolutionary', 'game-changing'];
    const moderatePositiveWords = ['excited', 'grateful', 'proud', 'inspiring', 'motivated', 'successful'];
    const weakPositiveWords = ['great', 'good', 'nice', 'awesome', 'wonderful'];
    
    const strongPositiveCount = strongPositiveWords.filter(word => lowerContent.includes(word)).length;
    const moderatePositiveCount = moderatePositiveWords.filter(word => lowerContent.includes(word)).length;
    const weakPositiveCount = weakPositiveWords.filter(word => lowerContent.includes(word)).length;
    
    score += Math.min(strongPositiveCount * 15, 30);
    score += Math.min(moderatePositiveCount * 10, 25);
    score += Math.min(weakPositiveCount * 5, 15);

    // Action words - require context
    const strongActionWords = ['transform', 'revolutionize', 'breakthrough', 'achieve', 'master'];
    const moderateActionWords = ['create', 'build', 'improve', 'grow', 'discover', 'launch', 'develop'];
    
    const strongActionCount = strongActionWords.filter(word => lowerContent.includes(word)).length;
    const moderateActionCount = moderateActionWords.filter(word => lowerContent.includes(word)).length;
    
    score += Math.min(strongActionCount * 12, 25);
    score += Math.min(moderateActionCount * 6, 20);

    // Check for storytelling elements - require depth
    const storyWords = ['story', 'journey', 'experience', 'lesson', 'challenge', 'overcome'];
    const storyCount = storyWords.filter(word => lowerContent.includes(word)).length;
    if (storyCount >= 2) score += 15;
    else if (storyCount === 1) score += 8;

    // Penalty for overuse of superlatives
    const superlatives = ['best', 'greatest', 'ultimate', 'perfect', 'incredible', 'amazing'];
    const superlativeCount = superlatives.filter(word => lowerContent.includes(word)).length;
    if (superlativeCount > 3) score -= 15;

    return Math.max(10, Math.min(100, score));
  }

  calculateOverallScore(analysis) {
    const baseScore = Object.entries(this.weights).reduce((total, [key, weight]) => {
      return total + (analysis[key] || 0) * weight;
    }, 0);
    
    // No bonus - strict scoring
    return Math.max(0, Math.min(100, baseScore));
  }

  getScoreBreakdown(analysis) {
    return Object.entries(this.weights).map(([category, weight]) => ({
      category: category.charAt(0).toUpperCase() + category.slice(1),
      score: analysis[category] || 0,
      weight: weight,
      contribution: Math.round((analysis[category] || 0) * weight),
      maxContribution: Math.round(100 * weight)
    }));
  }

  generateSuggestions(analysis, content, hasImage) {
    const suggestions = [];

    if (analysis.length < 70) {
      const charCount = content.length;
      if (charCount < 150) {
        suggestions.push({
          type: 'length',
          priority: 'high',
          icon: <ContentIcon />,
          title: 'Content too short',
          description: `${charCount} characters. Aim for 150-600 for optimal engagement`,
          action: 'Add more context, examples, or insights'
        });
      } else if (charCount > 2000) {
        suggestions.push({
          type: 'length',
          priority: 'medium',
          icon: <ContentIcon />,
          title: 'Content very long',
          description: `${charCount} characters. Consider breaking into multiple posts`,
          action: 'Shorten or split into a series of posts'
        });
      }
    }

    if (analysis.readability < 60) {
      suggestions.push({
        type: 'readability',
        priority: 'high',
        icon: <ContentIcon />,
        title: 'Improve readability',
        description: 'Sentences may be too complex or too simple',
        action: 'Use 8-15 words per sentence, avoid jargon'
      });
    }

    if (analysis.engagement < 60) {
      suggestions.push({
        type: 'engagement',
        priority: 'high',
        icon: <EngagementIcon />,
        title: 'Low engagement potential',
        description: 'Missing questions, calls-to-action, or personal elements',
        action: 'Add questions, ask for opinions, share experiences'
      });
    }

    if (analysis.structure < 50) {
      suggestions.push({
        type: 'structure',
        priority: 'medium',
        icon: <ContentIcon />,
        title: 'Poor structure',
        description: 'Content lacks clear organization',
        action: 'Use bullet points, paragraphs, and clear sections'
      });
    }

    if (analysis.hashtags < 60) {
      suggestions.push({
        type: 'hashtags',
        priority: 'medium',
        icon: <HashtagIcon />,
        title: 'Hashtag optimization needed',
        description: 'Use 3-5 relevant hashtags for better reach',
        action: 'Add industry-specific and trending hashtags'
      });
    }

    if (analysis.emotion < 50) {
      suggestions.push({
        type: 'emotion',
        priority: 'medium',
        icon: <TipIcon />,
        title: 'Lacks emotional appeal',
        description: 'Content feels flat or generic',
        action: 'Add personal stories, emotions, or strong language'
      });
    }

    // Visual content suggestions removed - focusing on text content only

    // Sort by priority and return top suggestions
    const priorityOrder = { high: 3, medium: 2, low: 1 };
    return suggestions
      .sort((a, b) => priorityOrder[b.priority] - priorityOrder[a.priority])
      .slice(0, 4);
  }

  identifyStrengths(analysis) {
    const strengths = [];

    if (analysis.length >= 85) {
      strengths.push({ type: 'length', text: 'Optimal length', score: analysis.length });
    }

    if (analysis.readability >= 80) {
      strengths.push({ type: 'readability', text: 'Highly readable', score: analysis.readability });
    }

    if (analysis.engagement >= 80) {
      strengths.push({ type: 'engagement', text: 'High engagement potential', score: analysis.engagement });
    }

    if (analysis.structure >= 80) {
      strengths.push({ type: 'structure', text: 'Well-structured', score: analysis.structure });
    }

    if (analysis.hashtags >= 85) {
      strengths.push({ type: 'hashtags', text: 'Excellent hashtag usage', score: analysis.hashtags });
    }

    if (analysis.emotion >= 80) {
      strengths.push({ type: 'emotion', text: 'Strong emotional appeal', score: analysis.emotion });
    }

    return strengths;
  }

  getDetailedMetrics(content, hashtags = []) {
    const words = content.split(/\s+/).filter(w => w.length > 0);
    const sentences = content.split(/[.!?]+/).filter(s => s.trim().length > 0);
    const contentHashtags = (content.match(/#[\w-]+/g) || []).length;
    const questions = (content.match(/\?/g) || []).length;
    const emojis = (content.match(/[\u{1F600}-\u{1F64F}]|[\u{1F300}-\u{1F5FF}]|[\u{1F680}-\u{1F6FF}]|[\u{2600}-\u{26FF}]/gu) || []).length;

    return {
      characters: content.length,
      words: words.length,
      sentences: sentences.length,
      hashtags: contentHashtags + hashtags.length,
      questions: questions,
      emojis: emojis,
      avgWordsPerSentence: sentences.length > 0 ? Math.round(words.length / sentences.length) : 0,
      readingTime: Math.ceil(words.length / 200)
    };
  }

  getGrade(score) {
    if (score >= 90) return { letter: 'A+', color: '#22C55E', description: 'Exceptional' };
    if (score >= 85) return { letter: 'A', color: '#22C55E', description: 'Excellent' };
    if (score >= 80) return { letter: 'A-', color: '#22C55E', description: 'Very Good' };
    if (score >= 75) return { letter: 'B+', color: '#65A30D', description: 'Good' };
    if (score >= 70) return { letter: 'B', color: '#F59E0B', description: 'Above Average' };
    if (score >= 65) return { letter: 'B-', color: '#F59E0B', description: 'Average' };
    if (score >= 60) return { letter: 'C+', color: '#F59E0B', description: 'Below Average' };
    if (score >= 55) return { letter: 'C', color: '#EF4444', description: 'Poor' };
    if (score >= 50) return { letter: 'C-', color: '#EF4444', description: 'Very Poor' };
    return { letter: 'F', color: '#DC2626', description: 'Needs Major Improvement' };
  }

  estimateEngagement(score, hasImage) {
    // More realistic engagement estimation
    let baseEngagement = Math.max(0, (score - 20) * 0.5);
    if (hasImage) baseEngagement *= 1.8;
    
    const estimatedLikes = Math.round(baseEngagement * 0.65);
    const estimatedComments = Math.round(baseEngagement * 0.25);
    const estimatedShares = Math.round(baseEngagement * 0.10);

    return {
      total: estimatedLikes + estimatedComments + estimatedShares,
      likes: estimatedLikes,
      comments: estimatedComments,
      shares: estimatedShares
    };
  }

  getEmptyScore() {
    return {
      overallScore: 0,
      categoryScores: {
        length: 0,
        readability: 0,
        engagement: 0,
        structure: 0,
        hashtags: 0,
        emotion: 0,
        visual: 0
      },
      suggestions: [],
      strengths: [],
      metrics: { characters: 0, words: 0, sentences: 0, hashtags: 0, questions: 0, emojis: 0, avgWordsPerSentence: 0, readingTime: 0 },
      grade: { letter: 'N/A', color: '#94A3B8', description: 'Not started' },
      estimatedEngagement: { total: 0, likes: 0, comments: 0, shares: 0 },
      breakdown: []
    };
  }
}

// Create scorer instance
const contentScorer = new StrictContentScorer();

const ContentScorer = ({ content = '', hasImage = false, hashtags = [], isVisible = true }) => {
  const [analysis, setAnalysis] = useState(contentScorer.getEmptyScore());
  const [anchorEl, setAnchorEl] = useState(null);

  // Only show if there's generated content (not just topic)
  const shouldShow = content && content.length > 20 && isVisible;

  // Analyze content in real-time
  useEffect(() => {
    if (shouldShow) {
      const newAnalysis = contentScorer.analyzeContent(content, hasImage, hashtags);
      setAnalysis(newAnalysis);
    }
  }, [content, hasImage, hashtags, shouldShow]);

  const getScoreColor = (score) => {
    if (score >= 80) return '#22C55E';
    if (score >= 70) return '#65A30D';
    if (score >= 60) return '#F59E0B';
    return '#EF4444';
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const open = Boolean(anchorEl);

  if (!shouldShow) return null;

  return (
    <>
      {/* Compact Score Display */}
      <Tooltip title="Click for detailed analysis">
        <Chip
          icon={<AIIcon />}
          label={`${analysis.overallScore} (${analysis.grade.letter})`}
          onClick={handleClick}
          sx={{
            bgcolor: analysis.grade.color,
            color: 'white',
            fontWeight: 600,
            cursor: 'pointer',
            fontSize: '0.85rem',
            height: 32,
            transition: 'all 0.2s ease',
            '&:hover': {
              transform: 'scale(1.05)',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)'
            },
            '& .MuiChip-icon': {
              color: 'white',
              fontSize: 18
            }
          }}
        />
      </Tooltip>

      {/* Detailed Popup */}
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'center',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'center',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            maxWidth: 500,
            maxHeight: 700,
            overflow: 'auto'
          }
        }}
      >
        <Box sx={{ p: 3 }}>
          {/* Header */}
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, textAlign: 'center' }}>
              Content Analysis
            </Typography>
          </Box>

          {/* Score Display */}
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography 
              variant="h2" 
              sx={{ 
                fontWeight: 700,
                color: getScoreColor(analysis.overallScore),
                lineHeight: 1
              }}
            >
              {analysis.overallScore}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {analysis.grade.description}
            </Typography>
          </Box>

          {/* Score Breakdown */}
          <Accordion sx={{ mb: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <CalculateIcon sx={{ mr: 1, fontSize: 20 }} />
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Score Breakdown
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="center">Score</TableCell>
                      <TableCell align="center">Weight</TableCell>
                      <TableCell align="center">Points</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {analysis.breakdown.map((item) => (
                      <TableRow key={item.category}>
                        <TableCell>{item.category}</TableCell>
                        <TableCell align="center">{item.score}</TableCell>
                        <TableCell align="center">{Math.round(item.weight * 100)}%</TableCell>
                        <TableCell align="center">
                          <Typography 
                            variant="body2" 
                            sx={{ 
                              fontWeight: 600,
                              color: getScoreColor(item.score)
                            }}
                          >
                            {item.contribution}/{item.maxContribution}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>

          {/* Metrics */}
          <Box sx={{ 
            display: 'grid', 
            gridTemplateColumns: 'repeat(3, 1fr)', 
            gap: 1, 
            mb: 3,
            p: 2,
            bgcolor: 'rgba(0, 0, 0, 0.02)',
            borderRadius: 2
          }}>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {analysis.metrics.characters}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Characters
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {analysis.metrics.words}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Words
              </Typography>
            </Box>
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {analysis.metrics.questions}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Questions
              </Typography>
            </Box>
          </Box>

          {/* Strengths */}
          {analysis.strengths.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#059669', mb: 1 }}>
                ✓ Strengths
              </Typography>
              <Stack spacing={0.5}>
                {analysis.strengths.map((strength, index) => (
                  <Chip
                    key={index}
                    label={`${strength.text} (${strength.score})`}
                    size="small"
                    sx={{
                      bgcolor: 'rgba(5, 150, 105, 0.1)',
                      color: '#059669',
                      fontWeight: 500,
                      alignSelf: 'flex-start'
                    }}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Suggestions */}
          {analysis.suggestions.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 600, color: '#DC2626', mb: 1 }}>
                ⚠️ Improvements Needed
              </Typography>
              <List dense sx={{ p: 0 }}>
                {analysis.suggestions.map((suggestion, index) => (
                  <ListItem key={index} sx={{ px: 0, py: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <Box sx={{ 
                        width: 8, 
                        height: 8, 
                        borderRadius: '50%', 
                        bgcolor: suggestion.priority === 'high' ? '#DC2626' : suggestion.priority === 'medium' ? '#F59E0B' : '#6B7280'
                      }} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          {suggestion.title}
                        </Typography>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          {suggestion.description}
                        </Typography>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Engagement Prediction */}
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body2" sx={{ fontWeight: 600, color: '#4F46E5', mb: 1 }}>
              Predicted Engagement
            </Typography>
            <Typography variant="h6" sx={{ color: '#4F46E5' }}>
              {analysis.estimatedEngagement.total}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Total interactions expected
            </Typography>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default ContentScorer; 